import { settings } from 'settings';
import Fetch from 'utils/fetch';
import {
  Account,
  AccountCategory,
  PartnerInviteResponse,
  Role,
  User,
  UserList,
} from 'types/userTypes';
import { plainToClass } from 'class-transformer';
import { CourierJson } from '../types/bundleTypes';
import { Country, Region } from '../types/orderTypes';
import { ERROR } from './../const/error';

// Get info about the logged in user
export const fetchMyself = async (): Promise<User> => {
  const url = `${settings.newApiUrl}/user/me`;
  const response = await Fetch.get({ url });
  return response.json();
};

export async function fetchUsers(
  page: number,
  searchText: string,
  roleFilter: Role[],
  regionFilter: Region | null | undefined,
  countryFilter: Country | null | undefined,
  locked: boolean
): Promise<UserList> {
  try {
    const params = {
      page: page,
      search: encodeURIComponent(searchText),
      roles: roleFilter.length === 0 ? '' : roleFilter.join(','),
      locked: locked ? 'true' : 'false',
      region: regionFilter || undefined,
      country: countryFilter || undefined,
    } as Record<string, any>;
    const urlParams = Object.keys(params)
      .filter(key => !!params[key])
      .map(key => `${key}=${params[key]}`)
      .join('&');

    const url = `${settings.newApiUrl}/user?${urlParams}`;
    const response = await Fetch.get({ url });

    if (response.ok) {
      const usersJson = await response.json();
      return plainToClass(UserList, usersJson);
    } else {
      return new UserList();
    }
  } catch (e) {
    console.log(ERROR.FETCH_USERS, e);
    return plainToClass(UserList, {});
  }
}

export async function fetchMyCourierUser(): Promise<User | undefined> {
  try {
    const url = `${settings.newApiUrl}/internal/courier/mobile/user`;
    const response = await Fetch.get({ url });

    if (response.ok) {
      const userJson = await response.json();
      return plainToClass(User, userJson);
    } else {
      return new User();
    }
  } catch (e) {
    console.log(ERROR.FETCH_USERS, e);
  }
}

export async function fetchCourierById(
  courierId: number
): Promise<User | undefined> {
  try {
    const url = `${settings.newApiUrl}/courier/${courierId}`;
    const response = await Fetch.get({ url });

    if (response.ok) {
      const userJson = await response.json();
      return plainToClass(User, userJson);
    } else {
      return new User();
    }
  } catch (e) {
    console.log(ERROR.FETCH_USERS, e);
  }
}

export async function fetchCourierByIdList(
  courierIdList: number[]
): Promise<{ [courierId: string]: CourierJson } | undefined> {
  try {
    const response: Response = await Fetch.post({
      url: `${settings.newApiUrl}/courier/list`,
      body: { courierIdList },
    });

    if (response.ok) return await response.json();
    return {};
  } catch (e) {
    console.log(ERROR.FETCH_USERS, e);
  }
}

export async function fetchAllCouriers(): Promise<CourierJson[] | undefined> {
  try {
    const url = `${settings.newApiUrl}/courier`;
    const response: Response = await Fetch.get({ url });

    if (response.ok) return await response.json();
    return [];
  } catch (e) {
    console.log(ERROR.FETCH_USERS, e);
  }
}

export async function fetchAccounts(
  accountType?: AccountCategory
): Promise<Account[] | undefined> {
  const accountParam = accountType ? `?accountType=${accountType}` : '';
  try {
    const url = `${settings.newApiUrl}/account/list${accountParam}`;
    const response = await Fetch.get({ url });

    if (response.ok) {
      const accountsJson: Account[] = await response.json();
      return plainToClass(Account, accountsJson);
    } else {
      return [];
    }
  } catch (e) {
    console.log(ERROR.FETCH_ACCOUNTS, e);
  }
}

export async function sendPartnerInvite(
  email: string
): Promise<PartnerInviteResponse> {
  const url = `${settings.newApiUrl}/user/invite`;
  const res = await Fetch.post({ url, body: { email } });

  if (res.ok) return res.json();
  const resText = await res.text();
  const msg = `The server responded with status ${res.status} and the following message: ${resText}`;
  throw Error(msg);
}
